import { ReactNode } from 'react';
import { Button, Flex, Text } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { GreyBox, WhiteSquare } from './commonComponents.tsx';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme.ts';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';

interface Props {
	title: string | ReactNode;
	currentPage?: number;
	totalPages?: number;
	onBackArrowClick?: () => void;
	onUpscale: () => void;
	onDownscale: () => void;
	onDownload?: () => void;
	onPrint?: () => void;
	onToggleDetails?: () => void;
	className?: string;
	showBackButton?: boolean;
}

export const DocumentViewerHeader = ({
	title,
	currentPage,
	totalPages,
	onBackArrowClick,
	onUpscale,
	onDownscale,
	onDownload,
	onPrint,
	onToggleDetails,
	showBackButton = true,
	className,
}: Props) => {
	const tCommonUi = useTranslationCommonUi();

	return (
		<GreyBox className={className}>
			<Flex gap="1rem" align="center" justify="space-between">
				{showBackButton && (
					<Flex gap="0.5rem" align="center" w="33%" mr="1rem">
						{!!onBackArrowClick && (
							<Icon variant="arrow-left" size="vlg" onClick={onBackArrowClick} />
						)}

						{typeof title === 'string' ? (
							<Text size="B5Medium" truncate lineClamp={1}>
								{title}
							</Text>
						) : (
							title
						)}
					</Flex>
				)}

				<Flex
					gap="0.5rem"
					align="center"
					justify={showBackButton ? 'center' : 'flex-start'}
					w="33%"
				>
					{!!currentPage && (
						<>
							<Text size="B5Medium">{tCommonUi('preview_pdf.page')}</Text>
							<WhiteSquare>
								<Text size="B6Medium">{currentPage}</Text>
							</WhiteSquare>
							<Text size="B5Medium">/</Text>
							<Text size="B5Medium">{totalPages}</Text>
						</>
					)}

					<Flex gap="1rem" align="center" ml="1rem">
						<Icon
							variant="minus"
							size="vlg"
							onClick={onDownscale}
							color={defaultTheme.v3.colors.dark[0]}
						/>

						<Icon
							variant="search-v3"
							size="xl"
							color={defaultTheme.v3.colors.dark[0]}
						/>

						<Icon
							variant="plus"
							size="vlg"
							onClick={onUpscale}
							color={defaultTheme.v3.colors.dark[0]}
						/>
					</Flex>
				</Flex>

				<Flex gap="0.5rem" align="center" justify="end" w="33%" ml="1rem">
					{!!onDownload && (
						<Button
							onClick={onDownload}
							variant="link-grey"
							leftSection={<Icon variant="download-v3" size="vlg" />}
						>
							{tCommonUi('preview_pdf.download')}
						</Button>
					)}
					{!!onPrint && (
						<Button
							onClick={onPrint}
							variant="link-grey"
							leftSection={<Icon variant="printer" size="vlg" />}
						>
							{tCommonUi('preview_pdf.print')}
						</Button>
					)}

					{!!onToggleDetails && (
						<Button
							onClick={onToggleDetails}
							variant="link-grey"
							leftSection={<Icon variant="information" size="vlg" />}
						>
							{tCommonUi('preview_pdf.details')}
						</Button>
					)}
				</Flex>
			</Flex>
		</GreyBox>
	);
};
